// frontend/src/pages/SafetyStockGenerator.jsx

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../hooks/useFormatters';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Stack,
} from '@mui/material';
import { ICON, Play } from '../icons';
import StatusChip from '../components/common/StatusChip';
import { AgGridReact } from 'ag-grid-react';
import Tile from '../components/layout/Tile';
import { useRunSafetyStockJob } from '../hooks/useInventoryMutations';
import { translateRequestError } from '../queries/api';
import { useAgGridTheme } from '../utils/agGridTheme';
import { defaultColDef, defaultGridProps, copyOnlyContextMenuItems, flexColumn } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import { useSsrmGrid } from '../hooks/useSsrmGrid';
import { useAuth } from '../contexts/AuthContext';
import '../styles/pages.css';
import '../styles/grid-page.css';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

const RESULTS_ENDPOINT = '/api/stock-calculations/results/';
// Rendered where a number the response owes us is absent, so it reads as
// missing instead of as zero.
const MISSING_VALUE = '—';
const PAGE_SIZE = 100;

function SafetyStockGenerator() {
  const { t } = useTranslation();
  const { userContext } = useAuth();
  const isStaff = userContext?.user?.is_staff === true;
  const { formatNumber } = useFormatters();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const gridState = useGridState('safety_stock');

  // Settings
  const [calculateSafetyStock, setCalculateSafetyStock] = useState(true);

  // Run state
  const runJobMutation = useRunSafetyStockJob();
  const processing = runJobMutation.isPending;
  const [result, setResult] = useState(null);

  // Grid state. The selected matrix cell is the grid's filter; the rows are
  // paged from the server through useSsrmGrid like every other grid, so only
  // the cell's row count is held here (from the response's `count`).
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellCount, setCellCount] = useState(null);

  const ssrmParams = useMemo(() => ({
    abc_class: selectedCell?.abc ?? null,
    xyz_class: selectedCell?.xyz ?? null,
    job_id: result?.job_id ?? null,
  }), [selectedCell, result?.job_id]);
  const onData = useCallback((data) => {
    if (typeof data?.count === 'number') setCellCount(data.count);
  }, []);
  const onError = useCallback(() => {
    showToast(t('safetyStockGenerator.failedLoadItems'), 'error');
    setSelectedCell(null);
  }, [t]);
  const { onGridReady, onGridPreDestroyed, loading: loadingItems, pagination } = useSsrmGrid({
    endpoint: RESULTS_ENDPOINT,
    params: ssrmParams,
    pageSize: PAGE_SIZE,
    onData,
    onError,
    enabled: !!selectedCell,
  });

  // Run classification
  const handleRunClassification = async () => {
    setResult(null);
    setSelectedCell(null);
    setCellCount(null);

    try {
      const data = await runJobMutation.mutateAsync({ calculateSafetyStock });
      setResult(data);
      // A completed run always reports both numbers
      // (backend/safety_stock_calculations/services.py run_stock_calculations
      // returns processed_items and execution_time on every attempt, and the
      // endpoint passes its dict through). A missing one is an upstream bug:
      // it is logged and rendered as missing, never replaced by a number the
      // merchant would act on. Presence is `!= null`, so a run that saved no
      // rows or finished in under a rounded second still reports its zero.
      const missing = ['processed_items', 'execution_time'].filter((key) => data?.[key] == null);
      if (missing.length > 0) {
        console.error('safety-stock run: response is missing', missing.join(', '), data?.job_id);
      }
      showToast(
        t('safetyStockGenerator.successMessage', {
          processedItems: data?.processed_items ?? MISSING_VALUE,
          seconds: data?.execution_time != null
            ? formatNumber(data.execution_time, 2)
            : MISSING_VALUE,
        }),
        'success'
      );
    } catch (err) {
      console.error('safety-stock run:', err.message);
      showToast(translateRequestError(err, t, 'safetyStockGenerator.failedMessage'), 'error');
    }
  };

  // Handle cell click — the cell becomes the grid's filter; useSsrmGrid
  // fetches page 1 for the new params and the grid pages from there.
  const handleCellClick = (abc, xyz, count) => {
    if (count === 0) return;
    setCellCount(null);
    setSelectedCell({ abc, xyz });
  };

  // AG-Grid columns
  const columns = React.useMemo(() => [
    {
      field: 'item_code',
      headerName: t('safetyStockGenerator.columns.itemCode'),
      ...flexColumn(),
    },
    {
      field: 'location_name',
      headerName: t('safetyStockGenerator.columns.location'),
      valueFormatter: (params) => params.value || params.data.location_code || '-',
    },
    {
      field: 'abc_class',
      headerName: t('safetyStockGenerator.columns.abc'),
      width: 80,
      suppressAutoSize: true,
    },
    {
      field: 'xyz_class',
      headerName: t('safetyStockGenerator.columns.xyz'),
      width: 80,
      suppressAutoSize: true,
    },
    {
      field: 'total_revenue',
      headerName: t('safetyStockGenerator.columns.revenue') + (result?.currency ? ` (${result.currency})` : ''),
      type: 'decimalColumn',
      valueFormatter: p => p.value != null ? formatNumber(p.value, 2) : '—',
    },
    // Staff-only figure: the server strips it for everyone else and refuses
    // to sort by it, since the row order would reveal it.
    ...(isStaff ? [{
      field: 'mape',
      headerName: t('safetyStockGenerator.columns.mape'),
      width: 110,
      suppressAutoSize: true,
      type: 'decimalColumn',
      valueFormatter: p => p.value != null ? `${formatNumber(p.value, 2)}%` : '—',
    }] : []),
    ...(calculateSafetyStock ? [
      {
        field: 'safety_stock',
        headerName: t('safetyStockGenerator.columns.safetyStock'),
        type: 'numberColumn',
        valueFormatter: p => p.value != null ? formatNumber(p.value, 0) : '—',
      },
      {
        field: 'lead_time_days',
        headerName: t('safetyStockGenerator.columns.leadTimeDays'),
        type: 'numberColumn',
        valueFormatter: p => p.value != null ? formatNumber(p.value, 0) : '—',
      },
      {
        field: 'review_period_days',
        headerName: t('safetyStockGenerator.columns.reviewPeriodDays'),
        headerTooltip: t('safetyStockGenerator.tooltips.reviewPeriodDays'),
        type: 'numberColumn',
        valueFormatter: p => p.value != null ? formatNumber(p.value, 0) : '—',
      },
    ] : []),
  ], [t, calculateSafetyStock, formatNumber, result?.currency, isStaff]);

  // Summary chips data
  const summaryChips = result ? [
    // `processed_items` is the classified count: the rows the run actually
    // saved, accumulated across chunks and whole-store on every attempt
    // (backend/safety_stock_calculations/services.py run_stock_calculations).
    // The other two counts in the same response are different quantities —
    // `summary.total_items` counts only the engine rows THIS attempt saw and is
    // absent on a resumed attempt, and top-level `total_items` counts the pairs
    // the job started with — so neither may stand in for it. Missing means an
    // upstream bug (logged at ERROR on arrival) and the chip is left out.
    ...(result.processed_items != null ? [{
      label: t('safetyStockGenerator.itemsClassified', { count: result.processed_items }),
      color: 'neutral',
    }] : []),
    // Read against the response shape the endpoint actually returns:
    // safety_stock_summary is a SIBLING of summary, not nested inside it, and
    // the lead-time mean is keyed avg_lead_time_days.
    //
    // Presence is tested with != null rather than truthiness: a mean safety
    // stock of 0 is a real answer (a catalogue of zero-variability items), and
    // hiding the chip for it would report "not calculated" for a number that
    // was.
    ...(result.execution_time != null ? [{
      label: `${formatNumber(result.execution_time, 2)}s`,
      color: 'neutral',
    }] : []),
    ...(result.safety_stock_summary?.safety_stock_stats?.mean != null ? [{
      label: t('safetyStockGenerator.avgSafetyStock', { value: formatNumber(result.safety_stock_summary.safety_stock_stats.mean, 0) }),
      color: 'info',
    }] : []),
    ...(result.safety_stock_summary?.lead_time_stats?.avg_lead_time_days != null ? [{
      label: t('safetyStockGenerator.avgLeadTime', { value: formatNumber(result.safety_stock_summary.lead_time_stats.avg_lead_time_days, 0) }),
      color: 'info',
    }] : []),
    ...(result.safety_stock_summary?.lead_time_stats?.avg_review_period_days != null ? [{
      label: t('safetyStockGenerator.avgReviewPeriod', { value: formatNumber(result.safety_stock_summary.lead_time_stats.avg_review_period_days, 0) }),
      color: 'info',
    }] : []),
  ] : [];

  return (
    <div className="page-container">
      {/* Configuration Section */}
      <div className="tiles-row">
        <Tile width="full" title={t('safetyStockGenerator.title')}>
          <Stack spacing={2}>
            {/* Summary Chips (after run) */}
            {summaryChips.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {summaryChips.map((chip, idx) => (
                  <StatusChip
                    key={idx}
                    label={chip.label}
                    color={chip.color}
                  />
                ))}
              </Stack>
            )}

            {/* Settings */}
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }} className="generator-settings-row">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={calculateSafetyStock}
                    onChange={(e) => setCalculateSafetyStock(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('safetyStockGenerator.calculateSafetyStock')}
              />
            </Stack>
          </Stack>
        </Tile>
      </div>

      {/* Results Section */}
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('safetyStockGenerator.resultsTitle')}
          toolbar={
            <Button
              variant="contained"
              startIcon={<Play size={ICON.md} />}
              onClick={handleRunClassification}
              loading={processing}
            >
              {result ? t('safetyStockGenerator.runAgain') : t('safetyStockGenerator.runClassification')}
            </Button>
          }
        >
          <GridPage>
            {/* Before run: Info text */}
            {!result && !processing && (
              <div className="grid-page-loading">
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, textAlign: 'center' }}>
                  {t('safetyStockGenerator.preRunInfo')}
                </Typography>
              </div>
            )}

            {/* During run: Loading spinner */}
            {processing && (
              <div className="grid-page-loading">
                <CircularProgress size={40} />
                <Typography variant="h6" className="grid-page-loading-text">
                  {t('safetyStockGenerator.processing')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('safetyStockGenerator.processingNote')}
                </Typography>
              </div>
            )}

            {/* After run: Matrix + Grid */}
            {result && !processing && (
              <>
                {/* ABC/XYZ Matrix */}
                {result.summary?.combined_distribution && (
                  <div>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
                      {t('safetyStockGenerator.matrixTitle')}
                    </Typography>
                    <div className="abc-xyz-matrix-container">
                      <table>
                        <thead>
                          <tr>
                            <th>ABC/XYZ</th>
                            <th>X</th>
                            <th>Y</th>
                            <th>Z</th>
                          </tr>
                        </thead>
                        <tbody>
                          {['A', 'B', 'C'].map(abc => (
                            <tr key={abc}>
                              <td className="abc-xyz-matrix-row-label">{abc}</td>
                              {['X', 'Y', 'Z'].map(xyz => {
                                const count = result.summary.combined_distribution[abc]?.[xyz] || 0;
                                const isSelected = selectedCell?.abc === abc && selectedCell?.xyz === xyz;
                                const isClickable = count > 0;

                                return (
                                  <td
                                    key={xyz}
                                    onClick={() => handleCellClick(abc, xyz, count)}
                                    className={`abc-xyz-matrix-cell ${isClickable ? 'abc-xyz-matrix-cell-clickable' : ''} ${isSelected ? 'abc-xyz-matrix-cell-selected' : ''}`}
                                  >
                                    {count}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Selected cell header */}
                {selectedCell && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedCell.abc}/{selectedCell.xyz} {t('nav.items')} ({cellCount == null ? '...' : cellCount})
                  </Typography>
                )}

                {/* Items grid — the first page of a newly selected cell */}
                {selectedCell && loadingItems && cellCount == null && (
                  <div className="grid-page-loading">
                    <CircularProgress size={32} />
                    <Typography variant="body2" className="grid-page-loading-text">
                      {t('safetyStockGenerator.loadingItems')}
                    </Typography>
                  </div>
                )}

                {selectedCell && (
                  <GridViewport>
                    {gridTheme && (
                      <AgGridReact
                        {...defaultGridProps}
                        theme={gridTheme}
                        {...gridState}
                        onGridReady={onGridReady}
                        onGridPreDestroyed={onGridPreDestroyed}
                        columnDefs={columns}
                        defaultColDef={defaultColDef}
                        columnTypes={columnTypes}
                        rowModelType="serverSide"
                        {...pagination}
                        getContextMenuItems={copyOnlyContextMenuItems}
                      />
                    )}
                  </GridViewport>
                )}
              </>
            )}
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default SafetyStockGenerator;
